import { Eye, EyeOff, KeyRound, X } from 'lucide-react';
import { useState } from 'react';
import API from '../../lib/api';

// Super-admin-only password reset modal. Hits one of two endpoints depending
// on which user table the target lives in:
//   endpoint='users' → PUT /admin/users/:id/password   (customer accounts)
//   endpoint='team'  → PUT /admin/team/:id/password    (CA / Admin / Super Admin)
export default function ChangePasswordModal({ open, target, endpoint = 'users', onClose, onSuccess }) {
  const [password, setPassword]   = useState('');
  const [confirm,  setConfirm]    = useState('');
  const [show,     setShow]       = useState(false);
  const [saving,   setSaving]     = useState(false);
  const [error,    setError]      = useState('');

  if (!open || !target) return null;

  const reset = () => {
    setPassword('');
    setConfirm('');
    setError('');
    setShow(false);
  };

  const close = () => { reset(); onClose?.(); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      const url = endpoint === 'team'
        ? `/admin/team/${target.id}/password`
        : `/admin/users/${target.id}/password`;
      await API.put(url, { password });
      onSuccess?.();
      close();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update password.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-[#E8E6F8] w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E6F8]">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#6C63FF]" />
            <h3 className="font-heading font-semibold text-[#1A1A2E] text-base">Change Password</h3>
          </div>
          <button onClick={close} className="text-[#9CA3AF] hover:text-[#6B7280]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-lg px-3 py-2">
            <p className="text-xs text-[#6B7280] font-body">Setting password for</p>
            <p className="text-sm text-[#1A1A2E] font-body font-medium">
              {target.name || target.email}
            </p>
            <p className="text-xs text-[#9CA3AF] font-body">{target.email}</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-xs font-body p-2.5 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">
              New password
            </label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                autoFocus
                className="w-full h-10 pl-3 pr-10 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                tabIndex={-1}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-body text-[#4A4A60] mb-1.5">
              Confirm new password
            </label>
            <input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter the password"
              className="w-full h-10 px-3 rounded-lg border border-[#E8E6F8] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/30 focus:border-[#6C63FF]"
            />
          </div>

          <p className="text-[10px] text-[#9CA3AF] font-body">
            The user will not be notified. Share the new password through a secure channel.
          </p>

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 h-10 bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 text-white rounded-full text-sm font-body font-medium transition-colors"
            >
              {saving ? 'Updating…' : 'Update Password'}
            </button>
            <button
              type="button"
              onClick={close}
              className="h-10 px-5 border border-[#E8E6F8] rounded-full text-sm font-body text-[#6B7280] hover:bg-[#F5F4FB] transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
